/**
 * AURA Icon Button Control
 * Large toggleable button with icon + label, matching AccessiBe-style card layout.
 */

export interface IconButtonOptions {
  id: string;
  label: string;
  icon: string;
  checked?: boolean;
  onChange?: (checked: boolean) => void;
}

export const ICON_BUTTON_STYLES = `
  .aura-icon-btn-grid {
    display: grid !important;
    grid-template-columns: repeat(3, 1fr) !important;
    gap: 8px !important;
    width: 100% !important;
  }

  .aura-icon-btn-grid--cols-2 {
    grid-template-columns: repeat(2, 1fr) !important;
  }

  .aura-icon-btn {
    display: flex !important;
    flex-direction: column !important;
    align-items: center !important;
    justify-content: center !important;
    gap: 8px !important;
    padding: 14px 8px !important;
    min-height: 88px !important;
    background: var(--aura-bg-secondary, #f8f9fa) !important;
    border: 1px solid var(--aura-border, #dee2e6) !important;
    border-radius: var(--aura-radius-lg, 12px) !important;
    cursor: pointer !important;
    text-align: center !important;
    transition: all 150ms ease !important;
    box-sizing: border-box !important;
    width: 100% !important;
  }

  .aura-icon-btn:hover {
    background: var(--aura-bg-hover, #e9ecef) !important;
    border-color: var(--aura-text-muted, #6c757d) !important;
  }

  .aura-icon-btn:focus-visible {
    outline: 2px solid var(--aura-border-focus, #4a90d9) !important;
    outline-offset: 2px !important;
  }

  .aura-icon-btn--active {
    background: var(--aura-primary-light, #e8eef5) !important;
    border-color: var(--aura-primary, #1e3a5f) !important;
    border-width: 2px !important;
  }

  .aura-icon-btn--active:hover {
    background: var(--aura-primary-light, #e8eef5) !important;
    border-color: var(--aura-primary-hover, #2a4a73) !important;
  }

  .aura-icon-btn__icon {
    width: 28px !important;
    height: 28px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    color: var(--aura-text-muted, #6c757d) !important;
    flex-shrink: 0 !important;
  }

  .aura-icon-btn__icon svg {
    width: 28px !important;
    height: 28px !important;
  }

  .aura-icon-btn--active .aura-icon-btn__icon {
    color: var(--aura-primary, #1e3a5f) !important;
  }

  .aura-icon-btn__label {
    font-size: 12px !important;
    font-weight: 500 !important;
    color: var(--aura-text, #212529) !important;
    line-height: 1.2 !important;
    word-break: break-word !important;
  }

  .aura-icon-btn--active .aura-icon-btn__label {
    color: var(--aura-primary, #1e3a5f) !important;
    font-weight: 600 !important;
  }
`;

export function createIconButton(options: IconButtonOptions): {
  element: HTMLElement;
  styles: string;
  setChecked: (checked: boolean) => void;
} {
  const button = document.createElement('button');
  button.type = 'button';
  button.id = options.id;
  button.className = `aura-icon-btn${options.checked ? ' aura-icon-btn--active' : ''}`;
  button.setAttribute('role', 'switch');
  button.setAttribute('aria-checked', String(!!options.checked));
  button.setAttribute('aria-label', options.label);

  const iconWrapper = document.createElement('span');
  iconWrapper.className = 'aura-icon-btn__icon';
  iconWrapper.setAttribute('aria-hidden', 'true');
  iconWrapper.innerHTML = options.icon;

  const label = document.createElement('span');
  label.className = 'aura-icon-btn__label';
  label.textContent = options.label;

  button.appendChild(iconWrapper);
  button.appendChild(label);

  let isChecked = !!options.checked;

  function setChecked(checked: boolean): void {
    isChecked = checked;
    button.classList.toggle('aura-icon-btn--active', checked);
    button.setAttribute('aria-checked', String(checked));
  }

  button.addEventListener('click', () => {
    isChecked = !isChecked;
    setChecked(isChecked);
    options.onChange?.(isChecked);
  });

  return { element: button, styles: ICON_BUTTON_STYLES, setChecked };
}
